import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useChargePointMutations, useChargePoints, useChargerModels, useRemovalPreflight,
  useStations,
} from '../lib/queries';
import {
  CONNECTOR_LABEL, connectorTone, freeNow, isWaiting, maxKw, occupiedCount,
  placeOf, presenceNote, sharesPower, chargePointLabel, chargePointTone,
} from '../data/types';
import type { ChargePoint } from '../data/types';
import { ChargerCount, Chip, Empty, Loading, Pill, Tile } from '../components/ui';
import { errorText, loadFailedHint } from '../lib/errors';

type Filter = 'all' | 'live' | 'blocked';
type View = 'cards' | 'list';

const BLOCK_REASONS = [
  'Faulty hardware', 'Vandalised', 'Site closed', 'Grid / power issue', 'Decommissioned',
];


/**
 * Which site this unit stands on, and where it actually dials in from.
 *
 * Two different questions, and both are worth answering. The site is where we
 * put it; the origin address is where the hardware really is. A unit answering
 * from another country than its site is the case this exists to make visible.
 */
function Where({ s }: { s: ChargePoint }) {
  return (
    <>
      {s.station
        ? (
          <div className="li-sub">
            {s.station.name}
            {s.station.city ? ` · ${s.station.city}` : ''}
          </div>
        )
        : <div className="li-sub" style={{ color: 'var(--warn, var(--bad))' }}>not placed on a station</div>}
      {s.origin && (
        <div className="li-sub mono" title={`last connected ${new Date(s.origin.ipLastSeen).toLocaleString()}`}>
          {s.origin.ip} · {placeOf(s.origin)}
        </div>
      )}
      {!s.origin && !s.preRegistered && (
        <div className="li-sub faint">no connection recorded</div>
      )}
      {s.preRegistered && (
        <div className="li-sub faint">pre-registered · has never connected</div>
      )}
    </>
  );
}

/** A connector's description, saying plainly where the charger has told us
 *  nothing rather than filling the gap with a default. */
function ConnectorSpec({ type, powerKw, current }: {
  type: string | null; powerKw: number | null; current: 'AC' | 'DC' | null;
}) {
  const bits = [
    powerKw != null ? `${powerKw} kW` : null,
    current,
  ].filter(Boolean).join(' · ');
  return (
    <div className="li-sub">
      {type ?? <span className="faint">type not reported</span>}
      {bits ? ` · ${bits}` : <span className="faint"> · power not reported</span>}
    </div>
  );
}

/** The reason a charge point is unusable, where there is one worth reading. */
function Why({ s }: { s: ChargePoint }) {
  const note = presenceNote(s);
  if (!note) return null;
  return <div className="li-sub" style={{ color: 'var(--bad)' }}>{note}</div>;
}


/**
 * Renames a charge point for everyone who reads a screen.
 *
 * This is a display name held in our own database, laid over whatever the CSMS
 * reports. It deliberately does NOT touch `ocppConnectionName` — that is the
 * identity the charger presents from its firmware on every connect, and
 * rewriting it here would not rename anything, it would just stop the next
 * connection from matching a known station.
 */
function Rename({ station, pending, error, onClose, onSubmit }: {
  station: ChargePoint;
  pending: boolean;
  /** The server's refusal, already in words. Without it a refused save left
   *  the dialog open and silent, which reads as the button not working. */
  error: string | null;
  onClose: () => void;
  onSubmit: (v: {
    displayName: string;
    manufacturer: string | null;
    model: string | null;
    serialNumber: string | null;
  }) => void;
}) {
  const [name, setName] = useState(station.name);
  /* Seeded with whatever is shown today, which is the operator's value if one
     was recorded and the charger's own report otherwise. Clearing a field
     stores null and lets the charger's value show through again, rather than
     writing an empty string that would hide it. */
  const [vendor, setVendor] = useState(station.vendor ?? '');
  const [model, setModel] = useState(station.model ?? '');
  const [serial, setSerial] = useState(station.serialNumber ?? '');
  const said = station.reported;
  /* The model is chosen from the catalog rather than typed, because the server
     keys a model policy off this exact string — a typo used to drop the unit
     onto handling that trusts what its connector says about a session. See
     screens/ChargerModels.tsx. */
  const { data: models } = useChargerModels();
  const known = models ?? [];
  const unrecognised = !!model && !known.some((m) => m.model === model);
  const trimmed = name.trim();
  const bad = trimmed.length < 2 ? 'Give it at least two characters.'
    : trimmed.length > 60 ? 'Keep it under 60 characters so it fits a card.'
      : null;

  return (
    <div
      role="dialog"
      aria-label="Rename charge point"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 420 }}>
        <div className="li-title" style={{ fontSize: 15 }}>Edit this charge point</div>
        <div className="li-sub" style={{ marginBottom: 10 }}>
          The name changes on every screen and in the driver app. The charger's
          own identity on the wire does not change.
        </div>

        <label className="li-sub" htmlFor="rn">Name</label>
        <input
          id="rn" className="field" value={name} autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || bad) return;
            onSubmit({
              displayName: trimmed,
              manufacturer: vendor.trim() || null,
              model: model.trim() || null,
              serialNumber: serial.trim() || null,
            });
          }}
        />

        <div style={{ marginTop: 14, marginBottom: 4 }} className="li-title">
          The hardware
        </div>
        <div className="li-sub" style={{ marginBottom: 8 }}>
          The charger reports these when it connects, but OCPP allows only 20
          characters for the make and model, so most units abbreviate. What you
          put here is what the console and any paperwork will show. Leave a
          field empty to use what the charger said.
          {' '}
          The model is picked from the catalog rather than typed: how a charger
          is handled when it drops a session depends on which model it is set
          to, so a name that matches nothing would be handled by the default
          rules without saying so.
        </div>

        <label className="li-sub" htmlFor="cp-make">Manufacturer</label>
        <input
          id="cp-make" className="field" value={vendor}
          placeholder={said?.vendor ?? 'YCL'}
          onChange={(e) => setVendor(e.target.value)}
        />

        <label className="li-sub" htmlFor="cp-model" style={{ marginTop: 8 }}>Model</label>
        <select
          id="cp-model" className="field" value={model}
          onChange={(e) => {
            const next = e.target.value;
            setModel(next);
            /* Keep the make in step with the model instead of letting the two
               drift — a unit recorded as a YCL-dual-44KW built by somebody
               else is a record nobody can use. Still editable afterwards. */
            const picked = known.find((m) => m.model === next);
            if (picked) setVendor(picked.manufacturer);
          }}
        >
          {/* Empty means "whatever the charger reports", the same thing
              clearing the old text box did. */}
          <option value="">
            {said?.model ? `Use what the charger says — ${said.model}` : 'Not set'}
          </option>
          {known.filter((m) => m.active).map((m) => (
            <option key={m.model} value={m.model}>
              {m.manufacturer} {m.model}
              {m.powerKw ? ` · ${m.powerKw} kW` : ''}
              {m.connectors ? ` · ${m.connectors} guns` : ''}
            </option>
          ))}
          {/* A retired model still has to be selectable while a charge point
              names it, or opening this form would show it as something else. */}
          {known.filter((m) => !m.active && m.model === model).map((m) => (
            <option key={m.model} value={m.model}>
              {m.manufacturer} {m.model} · retired
            </option>
          ))}
          {/* And whatever is stored now, even if the catalog has never heard of
              it. Leaving it out would silently change the model on save, which
              is the failure this list exists to prevent. */}
          {unrecognised && <option value={model}>{model} · not in the catalog</option>}
        </select>
        {unrecognised && (
          <div className="li-sub" style={{ marginTop: 4 }}>
            This model is not in the catalog, so this charge point is handled by
            the default rules. Pick one from the list, or add it under Charger
            models — saving will be refused until it matches.
          </div>
        )}

        <label className="li-sub" htmlFor="cp-serial" style={{ marginTop: 8 }}>
          Serial number
        </label>
        <input
          id="cp-serial" className="field mono" value={serial}
          placeholder={said?.serialNumber ?? 'yc0117526070018'}
          onChange={(e) => setSerial(e.target.value)}
        />

        {/* What the unit itself said, so an operator can see what they are
            correcting instead of overwriting it blind. */}
        {said && (said.vendor || said.model || said.serialNumber) && (
          <div className="li-sub" style={{ marginTop: 8 }}>
            The charger reports{' '}
            <span className="mono">{said.vendor ?? '—'}</span> ·{' '}
            <span className="mono">{said.model ?? '—'}</span> ·{' '}
            <span className="mono">{said.serialNumber ?? '—'}</span>
          </div>
        )}

        <div className="note" style={{ marginTop: 12 }}>
          <div className="li-sub">
            Identity on the wire — unchanged, and not editable here:
          </div>
          <div className="mono">{station.chargePointId ?? station.id}</div>
          <div className="li-sub" style={{ marginTop: 6 }}>
            A charge point announces its own id from firmware on every connect.
            Renaming it here would not rename the charger — it would stop the
            next connection from matching a station we know about.
          </div>
        </div>

        {(bad || error) && (
          <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 8 }}>
            {bad ?? error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                  onClick={() => onSubmit({
                    displayName: trimmed,
                    manufacturer: vendor.trim() || null,
                    model: model.trim() || null,
                    serialNumber: serial.trim() || null,
                  })}>
            {pending ? 'Saving...' : 'Save'}
          </button>
          <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}


/** A modal that closes on backdrop click and on Escape, which every dialog on
 *  this screen wants and none of them had. */
function Modal({ label, children, onClose }: {
  label: string; children: React.ReactNode; onClose: () => void;
}) {
  return (
    <div
      role="dialog" aria-label={label}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 440, maxHeight: '86vh', overflow: 'auto' }}>
        {children}
      </div>
    </div>
  );
}

/**
 * The settings a charge point has that the CSMS knows nothing about.
 *
 * Which station it stands on is the one that matters. A unit registers itself
 * the moment it opens a websocket and lands nowhere, so until an operator
 * places it, it is hardware on the network belonging to no address.
 */
function EditChargePoint({ cp, stations, pending, error, onClose, onSubmit }: {
  cp: ChargePoint;
  stations: Array<{ id: string; name: string; city: string | null }>;
  pending: boolean;
  /** The server's refusal. The local checks below catch the obvious cases, but
   *  only the server knows a station is still Test or has no coordinates. */
  error: string | null;
  onClose: () => void;
  onSubmit: (v: {
    stationId: string | null; maxSimultaneous: number | null;
    priceMinor: number | null; currency: string | null;
    environment: 'test' | 'prod';
  }) => void;
}) {
  const [stationId, setStationId] = useState(cp.station?.id ?? '');
  const [environment, setEnvironment] = useState<'test' | 'prod'>(cp.environment ?? 'test');
  const [simul, setSimul] = useState(cp.maxSimultaneous ? String(cp.maxSimultaneous) : '');
  const [price, setPrice] = useState(cp.price ? String(cp.price) : '');
  const [currency, setCurrency] = useState(cp.currency ?? '');

  const simulN = simul.trim() === '' ? null : Number(simul);
  const priceN = price.trim() === '' ? null : Number(price);
  const bad = simulN != null && !(Number.isInteger(simulN) && simulN >= 1)
    ? 'Connectors at once must be a whole number, 1 or more.'
    : priceN != null && !(priceN >= 0) ? 'A price cannot be negative.'
      : currency.trim() !== '' && currency.trim().length !== 3
        ? 'A currency is three letters, like ETB.'
        /* Publishing a unit with no site publishes one the app cannot put on a
           map: it appears in the list with no address and nothing to navigate
           to. The server refuses it too; this says so before the round trip. */
        : environment === 'prod' && !stationId
          ? 'Give it a station before making it live - drivers need an address to navigate to.'
          : null;
  const station = stations.find((st) => st.id === stationId);

  return (
    <Modal label="Edit charge point" onClose={onClose}>
      <div className="li-title" style={{ fontSize: 15 }}>{cp.name}</div>
      <div className="li-sub" style={{ marginBottom: 12 }}>
        Everything here is ours. The charger reports its own vendor, model,
        firmware and connector states, and none of that is editable.
      </div>

      <label className="li-sub">Visible to drivers</label>
      <div className="chips" style={{ marginBottom: 4 }}>
        <Chip label="Test - console only" selected={environment === 'test'}
              onClick={() => setEnvironment('test')} />
        <Chip label="Live - in the driver app" selected={environment === 'prod'}
              onClick={() => setEnvironment('prod')} />
      </div>
      <div className="li-sub faint" style={{ marginBottom: 12 }}>
        A charge point registers itself the moment it connects, so this starts as
        Test. Nothing reaches the driver app until someone here says it is real,
        which is what keeps a bench rig off the map.
      </div>

      <label className="li-sub" htmlFor="cp-station">Station</label>
      <select id="cp-station" className="field" value={stationId} style={{ marginBottom: 4 }}
              onChange={(e) => setStationId(e.target.value)}>
        <option value="">- not placed -</option>
        {stations.map((st) => (
          <option key={st.id} value={st.id}>{st.name}{st.city ? ' - ' + st.city : ''}</option>
        ))}
      </select>
      <div className="li-sub faint" style={{ marginBottom: 10 }}>
        {stations.length ? 'The site this unit stands on.' : 'No stations yet - add one first.'}
      </div>

      <label className="li-sub" htmlFor="cp-simul">Connectors that can run at once</label>
      <input id="cp-simul" className="field mono" value={simul} inputMode="numeric"
             placeholder={String(Math.max(1, cp.connectors.length)) + ' (all of them)'}
             onChange={(e) => setSimul(e.target.value)} />
      <div className="li-sub faint" style={{ marginBottom: 10 }}>
        Most dual-gun DC units share one power module, so an idle gun beside a
        charging one cannot start. Leave empty if every bay is independent.
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 2 }}>
          <label className="li-sub" htmlFor="cp-price">Price per kWh</label>
          <input id="cp-price" className="field mono" value={price} inputMode="decimal"
                 placeholder="not set" onChange={(e) => setPrice(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label className="li-sub" htmlFor="cp-cur">Currency</label>
          <input id="cp-cur" className="field mono" value={currency} maxLength={3}
                 placeholder="ETB" onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
        </div>
      </div>
      <div className="li-sub faint" style={{ marginTop: 4 }}>
        Emptying a field now clears it. That used to be impossible, which is why
        a price of zero was the way to mean "no price".
      </div>

      {!bad && environment === 'prod' && priceN == null && (
        <div className="note" style={{ marginTop: 10 }}>
          <b>No price set.</b> Drivers will see this charge point with no price,
          and nothing will be billed for a session on it.
        </div>
      )}
      {!bad && environment === 'prod' && station && (
        <div className="note" style={{ marginTop: 10 }}>
          Saving makes this visible to every driver, at {station.name}
          {station.city ? ', ' + station.city : ''}.
        </div>
      )}

      {(bad || error) && (
        <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 8 }}>
          {bad ?? error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                onClick={() => onSubmit({
                  stationId: stationId || null,
                  maxSimultaneous: simulN,
                  priceMinor: priceN == null ? null : Math.round(priceN * 100),
                  currency: currency.trim() || null,
                  environment,
                })}>
          {pending ? 'Saving...' : 'Save'}
        </button>
        <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

/**
 * Record a charge point before it exists on the network.
 *
 * This does not create hardware. It records the identity we are about to flash
 * into a unit, so that when it first connects the two halves meet instead of it
 * arriving as an unplaced stranger beside the row prepared for it. Getting that
 * string wrong is the whole risk, which is why it is the one field with no
 * default and no forgiveness.
 */
function PreRegister({ stations, pending, error, onClose, onSubmit }: {
  stations: Array<{ id: string; name: string; city: string | null }>;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (v: { chargePointId: string; displayName?: string; stationId?: string }) => void;
}) {
  const [cpId, setCpId] = useState('');
  const [name, setName] = useState('');
  const [stationId, setStationId] = useState('');
  const id = cpId.trim();
  const bad = !id ? 'Enter the identity the charger will announce.'
    : !/^[A-Za-z0-9_.:-]{1,48}$/.test(id)
      ? 'Letters, digits and _ . : - only, up to 48 characters.' : null;

  return (
    <Modal label="Add charge point" onClose={onClose}>
      <div className="li-title" style={{ fontSize: 15 }}>Add a charge point</div>
      <div className="li-sub" style={{ marginBottom: 12 }}>
        A charger registers itself the first time it connects. Adding it here
        beforehand means it arrives already named and already on a station.
      </div>

      <label className="li-sub" htmlFor="pr-id">OCPP identity</label>
      <input id="pr-id" className="field mono" value={cpId} autoFocus
             placeholder="charge_station_02" onChange={(e) => setCpId(e.target.value)} />
      <div className="li-sub faint" style={{ marginBottom: 10 }}>
        Exactly the charge box id you will flash into the unit. It must match
        character for character.
      </div>

      <label className="li-sub" htmlFor="pr-name">Name (optional)</label>
      <input id="pr-name" className="field" value={name} placeholder="Bay 2, forecourt"
             onChange={(e) => setName(e.target.value)} style={{ marginBottom: 10 }} />

      <label className="li-sub" htmlFor="pr-station">Station (optional)</label>
      <select id="pr-station" className="field" value={stationId}
              onChange={(e) => setStationId(e.target.value)}>
        <option value="">- place it later -</option>
        {stations.map((st) => (
          <option key={st.id} value={st.id}>{st.name}{st.city ? ' - ' + st.city : ''}</option>
        ))}
      </select>

      {(bad || error) && (
        <div className="li-sub" style={{ color: 'var(--bad)', marginTop: 8 }}>{error ?? bad}</div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                onClick={() => onSubmit({
                  chargePointId: id,
                  displayName: name.trim() || undefined,
                  stationId: stationId || undefined,
                })}>
          {pending ? 'Adding...' : 'Add charge point'}
        </button>
        <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}


/**
 * Removing a charge point.
 *
 * Two gates, because this is the only irreversible thing on the console.
 *
 * The first is honesty: before anyone is asked to confirm, the server is asked
 * what would actually be deleted, and the answer is shown as row counts per
 * table. A confirmation that does not say what it destroys is a formality.
 *
 * The second is the identity, typed back. Not a checkbox — this network has
 * seven charge points whose names differ by a character or two, and the risk
 * being defended against is deleting the wrong one confidently.
 */
function RemoveChargePoint({ cp, pending, error, onClose, onConfirm }: {
  cp: ChargePoint;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (confirm: string, includeRecords: boolean) => void;
}) {
  const { data: pre, isLoading } = useRemovalPreflight(cp.id);
  const [typed, setTyped] = useState('');
  const [ack, setAck] = useState(false);
  const matches = typed.trim() === cp.id;

  /* Sessions are the only thing here that cannot come back, so they get their
     own acknowledgement on top of the typed identity. The identity answers
     "which charge point"; this answers "and its billing history too". */
  const destroys = pre?.warnings.length ? pre.warnings : [];
  const needsAck = destroys.length > 0;
  const ready = !!pre?.canRemoveWithRecords && matches && (!needsAck || ack);

  return (
    <Modal label="Remove charge point" onClose={onClose}>
      <div className="li-title" style={{ fontSize: 15 }}>Remove {cp.name}?</div>
      <div className="li-sub mono" style={{ marginBottom: 10 }}>{cp.id}</div>

      {isLoading || !pre ? (
        <div className="li-sub faint">Checking what this would delete…</div>
      ) : (
        <>
          {!pre.canRemoveWithRecords ? (
            <div className="note" style={{ marginBottom: 10 }}>
              <b>This charge point cannot be removed.</b>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {pre.blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
              <div className="li-sub" style={{ marginTop: 6 }}>
                A charger that is connected re-registers itself the moment it
                sends anything, so removing it now changes nothing. Take it off
                the network first, or block it — it disappears from the driver
                app and keeps its history.
              </div>
            </div>
          ) : (
            <>
              {needsAck && (
                <div className="note" style={{ marginBottom: 10 }}>
                  <b>This destroys billing history.</b>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {destroys.map((w) => <li key={w}>{w}</li>)}
                  </ul>
                  <div className="li-sub" style={{ marginTop: 6 }}>
                    Sessions are a record of something that happened to a
                    driver's money, and nothing brings them back. Blocking keeps
                    that history and still takes the charge point out of the
                    driver app. Remove it only if this unit's sessions are not
                    worth keeping — a test rig, or a charger that never billed
                    anyone.
                  </div>
                </div>
              )}

              <div className="li-sub" style={{ marginBottom: 8 }}>
                This deletes the following rows. It cannot be undone.
              </div>
              <div className="tbl" style={{ marginBottom: 10 }}>
                <table>
                  <tbody>
                    {pre.csms.map((d) => (
                      <tr key={d.table}>
                        <td className="mono">{d.table}</td>
                        <td className="num mono">{d.rows}</td>
                        <td className="li-sub">CSMS</td>
                      </tr>
                    ))}
                    {pre.existsInCsms && (
                      <tr>
                        <td className="mono">ChargingStations</td>
                        <td className="num mono">1</td>
                        <td className="li-sub">CSMS</td>
                      </tr>
                    )}
                    {pre.ours.overrides > 0 && (
                      <tr>
                        <td className="mono">charge_point_overrides</td>
                        <td className="num mono">{pre.ours.overrides}</td>
                        <td className="li-sub">ours — name, tariff, station</td>
                      </tr>
                    )}
                    {pre.ours.connectors > 0 && (
                      <tr>
                        <td className="mono">connector_overrides</td>
                        <td className="num mono">{pre.ours.connectors}</td>
                        <td className="li-sub">ours — bay descriptions</td>
                      </tr>
                    )}
                    {pre.ours.connections > 0 && (
                      <tr>
                        <td className="mono">station_connections</td>
                        <td className="num mono">{pre.ours.connections}</td>
                        <td className="li-sub">ours — where it dialled in from</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {pre.existsInCsms && (
                <div className="note" style={{ marginBottom: 10 }}>
                  <b>If this unit ever connects again, it comes straight back.</b> A
                  charger announces its own identity and the CSMS creates the row
                  on the spot — unnamed and on no station. Remove it here only if
                  the hardware is gone, or decommission it at the unit first.
                </div>
              )}

              {needsAck && (
                <label
                  className="li-sub"
                  style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}
                >
                  <input
                    id="rm-ack" type="checkbox" checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    I understand {pre.transactions} session
                    {pre.transactions === 1 ? '' : 's'} will be permanently
                    deleted along with this charge point.
                  </span>
                </label>
              )}

              <label className="li-sub" htmlFor="rm-confirm">
                Type <span className="mono">{cp.id}</span> to confirm
              </label>
              <input
                id="rm-confirm" className="field mono" value={typed} autoFocus
                autoComplete="off" spellCheck={false}
                onChange={(e) => setTyped(e.target.value)}
              />
            </>
          )}
        </>
      )}

      {error && (
        <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 8 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          className="btn"
          style={{ flex: 1 }}
          disabled={!ready || pending}
          onClick={() => onConfirm(typed.trim(), needsAck)}
        >
          {pending
            ? 'Removing…'
            : needsAck
              ? `Remove and delete ${pre?.transactions ?? 0} session${pre?.transactions === 1 ? '' : 's'}`
              : 'Remove permanently'}
        </button>
        <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

export function ChargePoints() {
  const { data: res, isLoading, error: loadError } = useChargePoints();
  const data = res?.items;
  const { block, unblock, update, preRegister, remove } = useChargePointMutations();
  const { data: stations } = useStations();
  const sites = (stations ?? []).map((st) => ({ id: st.id, name: st.name, city: st.city }));
  const [editing, setEditing] = useState<ChargePoint | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<ChargePoint | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [view, setView] = useState<View>('cards');
  const [blocking, setBlocking] = useState<ChargePoint | null>(null);
  const [renaming, setRenaming] = useState<ChargePoint | null>(null);
  const [reason, setReason] = useState(BLOCK_REASONS[0]!);
  const [note, setNote] = useState('');
  /* One slot for whichever of Rename / Site & tariff / Block is open — only
     one can be. Cleared on every open so a refusal from the last dialog does
     not greet the next one. */
  const [dialogError, setDialogError] = useState<string | null>(null);
  const openRename = (s: ChargePoint) => { setDialogError(null); setRenaming(s); };
  const openEdit = (s: ChargePoint) => { setDialogError(null); setEditing(s); };
  const openBlock = (s: ChargePoint) => {
    setDialogError(null); setBlocking(s); setReason(BLOCK_REASONS[0]!); setNote('');
  };
  const failed = (e: Error) => setDialogError(errorText(e));
  /* Reactivate has no dialog to hold a refusal, so it gets a line above the
     list instead of failing invisibly. */
  const [pageError, setPageError] = useState<string | null>(null);
  const reactivate = (id: string) => {
    setPageError(null);
    unblock.mutate(id, { onError: (e: Error) => setPageError(errorText(e)) });
  };

  if (isLoading) return <Loading />;
  /* A failed load used to fall through to the spinner and stay there. */
  if (!data) {
    return <Empty title="Charge points are not available." hint={loadFailedHint(loadError)} />;
  }

  const live = data.filter((s) => !s.blocked);
  const blockedCount = data.length - live.length;
  const shown = data.filter((s) =>
    filter === 'live' ? !s.blocked : filter === 'blocked' ? s.blocked : true);

  const totalConnectors = data.reduce((n, s) => n + s.connectors.length, 0);
  const freeTotal = data.reduce((n, s) => n + freeNow(s), 0);
  const occupiedTotal = data.reduce((n, s) => n + occupiedCount(s), 0);
  const unplaced = data.filter((s) => !s.station).length;

  const counts: Record<Filter, number> = { all: data.length, live: live.length, blocked: blockedCount };

  return (
    <>
      <div className="tiles">
        <Tile k="Charge points" v={data.length} d={`${live.filter((s) => s.status !== 'offline').length} online`} />
        <Tile k="Connectors" v={totalConnectors} d="bays across the network" />
        <Tile k="Unplaced" v={unplaced} d={unplaced ? 'on no station' : 'all placed'} />
        <Tile k="Free now" v={freeTotal} d="bays a driver can take" />
        {/* Occupied, not charging. A bay at Preparing is taken by someone
            standing at it, and this tile reading 0 beside a card badged "In
            use" is the console disagreeing with itself. */}
        <Tile k="In use" v={occupiedTotal} d="bays with a driver on them" />
        <Tile k="Blocked" v={blockedCount} d={blockedCount ? 'hidden from drivers' : 'none blocked'} />
      </div>

      <div className="toolbar">
        <div className="chips">
          {(['all', 'live', 'blocked'] as const).map((f) => (
            <Chip
              key={f}
              selected={filter === f}
              onClick={() => setFilter(f)}
              label={<>{f === 'all' ? 'All' : f === 'live' ? 'Active' : 'Blocked'} <span className="mono">{counts[f]}</span></>}
            />
          ))}
        </div>
        <div className="chips">
          {(['cards', 'list'] as const).map((v) => (
            <Chip key={v} selected={view === v} onClick={() => setView(v)} label={v === 'cards' ? 'Cards' : 'List'} />
          ))}
          <button className="btn sm" onClick={() => { setAdding(true); setAddError(null); }}>
            Add charge point
          </button>
        </div>
      </div>

      {pageError && (
        <div className="note" role="alert" style={{ marginTop: 12, color: 'var(--bad)' }}>{pageError}</div>
      )}

      {!shown.length && <Empty title="No charge points in this list." />}

      {view === 'cards' && !!shown.length && (
        <div className="station-grid">
          {shown.map((s) => (
            <div className={`scard${s.blocked ? ' blocked' : ''}`} key={s.id}>
              <div className="scard-head">
                <div style={{ minWidth: 0 }}>
                  <div className="li-title">{s.name} <ChargerCount station={s} /></div>
                  <Where s={s} />
                  <Why s={s} />
                </div>
                <Pill tone={chargePointTone(s)}>{chargePointLabel(s)}</Pill>
              </div>

              <div className="scard-meta">
                <Pill>{s.connectors.length} connectors</Pill>
                {sharesPower(s) && <Pill>{s.maxSimultaneous} at a time</Pill>}
                {maxKw(s) != null && <Pill>{maxKw(s)} kW</Pill>}
                <Pill>{(s.currency ?? 'ETB')} {s.price.toFixed(2)} / kWh</Pill>
              </div>

              {s.blocked && (
                <div className="note">
                  <b>Blocked</b> · {s.blockReason}
                  <br />
                  <span className="faint">{s.blockedAt} · by {s.blockedBy}</span>
                </div>
              )}

              <div className="conn-list">
                {s.connectors.map((c) => {
                  const waiting = isWaiting(s, c);
                  return (
                    <div className="conn" key={c.id}>
                      <span className="conn-id">C{c.id}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="li-title">
                          {c.label ?? c.type ?? `Connector ${c.id}`}
                          {c.reported === false && <span className="faint"> · declared</span>}
                        </div>
                        <ConnectorSpec type={c.type} powerKw={c.powerKw} current={c.current} />
                        {waiting && <div className="li-sub">shared power, waiting</div>}
                      </div>
                      <Pill tone={waiting ? 'mid' : connectorTone(c.status)}>
                        {waiting ? 'Waiting' : CONNECTOR_LABEL[c.status]}
                      </Pill>
                    </div>
                  );
                })}
              </div>

              <div className="scard-actions">
                {s.blocked ? (
                  <button className="btn sm" onClick={() => reactivate(s.id)} disabled={unblock.isPending}>
                    Reactivate
                  </button>
                ) : (
                  <button className="btn sm ghost" onClick={() => openBlock(s)}>
                    Block
                  </button>
                )}
                {/* Two different dialogs, so two different words. Both used to
                    say "Edit", and which one held the tariff was a guess. */}
                <button className="btn sm ghost" onClick={() => openRename(s)}>Rename / hardware</button>
                <button className="btn sm ghost" onClick={() => openEdit(s)}>Site &amp; tariff</button>
                {s.presence === 'online' && !s.blocked && (
                  <Link className="btn sm ghost" to={`/testbench?cp=${encodeURIComponent(s.id)}`}>
                    Test
                  </Link>
                )}
                {s.presence !== 'online' && (
                  <button className="btn sm ghost"
                          onClick={() => { setRemoving(s); setRemoveError(null); }}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'list' && !!shown.length && (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Charge point</th>
                <th className="num">Connectors</th>
                <th className="num">Peak</th>
                <th>Types</th>
                <th className="num">Tariff</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((s) => (
                <tr key={s.id} className={s.blocked ? 'off' : undefined}>
                  <td>
                    <div className="li-title">{s.name} <ChargerCount station={s} /></div>
                    <Where s={s} />
                  <Why s={s} />
                  </td>
                  <td className="num mono">{s.connectors.length}</td>
                  <td className="num mono">{maxKw(s) != null ? `${maxKw(s)} kW` : '—'}</td>
                  <td>
                    {[...new Set(s.connectors.map((c) => c.type).filter(Boolean))].join(' · ')
                      || <span className="faint">not reported</span>}
                  </td>
                  <td className="num mono">{s.price.toFixed(2)}</td>
                  <td>
                    <Pill tone={chargePointTone(s)}>{chargePointLabel(s)}</Pill>
                    {s.blocked && <div className="li-sub">{s.blockReason}</div>}
                  </td>
                  <td className="act">
                    {s.blocked ? (
                      <button className="btn sm" onClick={() => reactivate(s.id)} disabled={unblock.isPending}>Reactivate</button>
                    ) : (
                      <button className="btn sm ghost" onClick={() => openBlock(s)}>
                        Block
                      </button>
                    )}
                    <button className="btn sm ghost" onClick={() => openRename(s)}>Rename / hardware</button>
                    <button className="btn sm ghost" onClick={() => openEdit(s)}>Site &amp; tariff</button>
                    {s.presence === 'online' && !s.blocked && (
                      <Link className="btn sm ghost" to={`/testbench?cp=${encodeURIComponent(s.id)}`}>
                        Test
                      </Link>
                    )}
                    {s.presence !== 'online' && (
                      <button className="btn sm ghost"
                              onClick={() => { setRemoving(s); setRemoveError(null); }}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {removing && (
        <RemoveChargePoint
          cp={removing}
          pending={remove.isPending}
          error={removeError}
          onClose={() => setRemoving(null)}
          onConfirm={(confirm, includeRecords) => remove.mutate(
            { cpId: removing.id, confirm, includeRecords },
            {
              onSuccess: () => setRemoving(null),
              onError: (e: Error) => setRemoveError(
                e.message === 'has_transactions'
                  ? 'It has sessions recorded against it. Tick the box to delete them too.'
                  : e.message === 'online'
                    ? 'It reconnected while this dialog was open.'
                    : e.message === 'confirm_mismatch'
                      ? 'That is not the identity of this charge point.'
                      : e.message === 'csms_refused'
                        ? 'The CSMS refused: something still references it.'
                        : errorText(e)),
            },
          )}
        />
      )}

      {editing && (
        <EditChargePoint
          cp={editing}
          stations={sites}
          pending={update.isPending}
          error={dialogError}
          onClose={() => setEditing(null)}
          onSubmit={(v) => { setDialogError(null); update.mutate(
            { id: editing.id, ...v },
            { onSuccess: () => setEditing(null), onError: failed },
          ); }}
        />
      )}

      {adding && (
        <PreRegister
          stations={sites}
          pending={preRegister.isPending}
          error={addError}
          onClose={() => setAdding(false)}
          onSubmit={(v) => preRegister.mutate(v, {
            onSuccess: () => setAdding(false),
            onError: (e: Error) => setAddError(
              e.message === 'already_registered'
                ? 'That identity is already recorded.'
                : e.message === 'already_connected'
                  ? 'A charger with that identity has already connected, so it is on this list.'
                  : e.message === 'charge_point_id_charset'
                    ? 'Letters, digits and _ . : - only.'
                    : errorText(e)),
          })}
        />
      )}

      {renaming && (
        <Rename
          station={renaming}
          pending={update.isPending}
          error={dialogError}
          onClose={() => setRenaming(null)}
          onSubmit={(v) => { setDialogError(null); update.mutate(
            { id: renaming.id, ...v },
            { onSuccess: () => setRenaming(null), onError: failed },
          ); }}
        />
      )}

      {blocking && (
        <div
          role="dialog"
          aria-label="Block charger"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
            display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setBlocking(null); }}
        >
          <div className="card" style={{ width: '100%', maxWidth: 420 }}>
            <div className="li-title" style={{ fontSize: 15 }}>Block {blocking.name}</div>
            <div className="li-sub" style={{ marginBottom: 10 }}>
              Drivers stop seeing this charger straight away. A session already running is not cut off.
            </div>
            <div className="chips" style={{ marginBottom: 10 }}>
              {BLOCK_REASONS.map((r) => (
                <Chip key={r} label={r} selected={reason === r} onClick={() => setReason(r)} />
              ))}
            </div>
            <input
              className="field"
              placeholder="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            {dialogError && (
              <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 8 }}>
                {dialogError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                className="btn"
                style={{ flex: 1 }}
                disabled={block.isPending}
                onClick={() => {
                  setDialogError(null);
                  block.mutate(
                    { id: blocking.id, reason: note ? `${reason} · ${note}` : reason },
                    { onSuccess: () => setBlocking(null), onError: failed },
                  );
                }}
              >
                {block.isPending ? 'Blocking...' : 'Block charger'}
              </button>
              <button className="btn ghost" style={{ flex: 1 }} onClick={() => setBlocking(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
